/**
 * Redux slice for managing chat mode state (Ask Chat vs RAG modes like 3M Knowledge)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import logger from '../utils/logger';

export type ChatMode = 'Ask Chat' | '3M Knowledge';
export type RagChatType = 'Everyday Insights' | 'Legal' | 'DevOps' | 'TEBG' | 'ITSM' | 'JIRA' | null;

// Maximum number of messages to keep in history (user + assistant pairs)
// 100 messages = ~50 conversation turns, which should be sufficient for context
const MAX_HISTORY_SIZE = 100;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string | null;
  isVoice?: boolean; // Flag for voice messages
  isTranscribing?: boolean; // Flag for user messages still being transcribed
  audioData?: string | string[]; // Audio data: either concatenated base64 string OR array of base64 chunks
  functionCall?: {
    type: 'web_search' | 'unknown'; // Type of function being called
    message: string; // Original message from backend
    isActive?: boolean; // If the function is currently executing
  };
  sources?: Array<{ // Sources returned from function calls (e.g., web search results)
    title: string;
    url: string;
    snippet?: string;
  }>;
  translationData?: Array<{ // optional translation file data for download buttons
    filename: string;
    content_base64: string;
    language: string;
  }>;
  [key: string]: any; // Allow additional properties like tools, timestamps, etc.
}

export interface ChatModeState {
  mode: ChatMode;
  ragChatType: RagChatType; // Active RAG chat within 3M Knowledge mode
  askChatHistory: ChatMessage[];
  ragHistory: ChatMessage[];
  isStreaming: boolean; // Track if any chat mode is currently streaming
}

const initialState: ChatModeState = {
  mode: 'Ask Chat',
  ragChatType: null, // Start with no RAG selected (Home is active)
  askChatHistory: [],
  ragHistory: [],
  isStreaming: false,
};

const chatModeSlice = createSlice({
  name: 'chatMode',
  initialState,
  reducers: {
    setChatMode: (state, action: PayloadAction<ChatMode>) => {
      state.mode = action.payload;
      // Do NOT reset ragChatType when switching to Ask Chat — preserve it so
      // returning to 3M Knowledge restores the previously selected RAG.
      // ragChatType is only cleared explicitly (e.g. clicking 3M Knowledge
      // while already on 3M Knowledge dispatches setRagChatType(null) directly).
    },
    setRagChatType: (state, action: PayloadAction<RagChatType>) => {
      state.ragChatType = action.payload;
      // Automatically switch to 3M Knowledge mode when selecting any RAG chat
      if (action.payload !== null) {
        state.mode = '3M Knowledge';
      }
    },
    setaskChatHistory: (state, action: PayloadAction<ChatMessage[]>) => {
      const newHistory = action.payload;
      // If history exceeds limit, keep only the most recent messages
      if (newHistory.length > MAX_HISTORY_SIZE) {
        state.askChatHistory = newHistory.slice(newHistory.length - MAX_HISTORY_SIZE);
      } else {
        state.askChatHistory = newHistory;
      }
    },
    setRagHistory: (state, action: PayloadAction<ChatMessage[]>) => {
      const newHistory = action.payload;
      // If history exceeds limit, keep only the most recent messages
      if (newHistory.length > MAX_HISTORY_SIZE) {
        state.ragHistory = newHistory.slice(newHistory.length - MAX_HISTORY_SIZE);
      } else {
        state.ragHistory = newHistory;
      }
    },
    clearaskChatHistory: (state) => {
      state.askChatHistory = [];
    },
    clearRagHistory: (state) => {
      state.ragHistory = [];
    },
    setIsStreaming: (state, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload;
    },
    // Voice-specific actions
    addVoiceUserMessage: {
      reducer: (state, action: PayloadAction<{ id?: string }>) => {
        // Add a "transcribing" placeholder for user's voice message
        // Always use timestamp-based ID for consistent sorting
        const timestamp = Date.now();
        const messageId = `voice-user-${timestamp}`;
        const newMessage: ChatMessage = {
          id: messageId,
          role: 'user',
          content: 'Transcribing...',
          isVoice: true,
          isTranscribing: true,
          turnId: action.payload.id, // Store original turn ID for matching
        };
        
        const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
        const updatedHistory = [...history, newMessage];
        
        if (state.mode === 'Ask Chat') {
          state.askChatHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
        } else {
          state.ragHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
        }
      },
      prepare: (payload?: { id?: string }) => {
        return { payload: payload || {} };
      }
    },
    addVoiceAssistantMessage: (state) => {
      // Add an empty assistant message that will be updated with text deltas
      const newMessage: ChatMessage = {
        id: `voice-assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        isVoice: true,
      };
      
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      const updatedHistory = [...history, newMessage];
      
      if (state.mode === 'Ask Chat') {
        state.askChatHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
      } else {
        state.ragHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
      }
    },
    updateLastAssistantMessage: (state, action: PayloadAction<{ textDelta: string }>) => {
      // Append text delta to the last assistant message
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      
      if (history.length > 0 && history[history.length - 1].role === 'assistant') {
        const lastMessage = history[history.length - 1];
        
        // Smart spacing: add space ONLY after web search completion (when function call was active)
        // This prevents "Minnesota.Right" and creates "Minnesota. Right" after web search
        const needsSpaceAfterFunctionCall = 
          lastMessage.functionCall?.isActive === true && 
          (lastMessage.content?.length ?? 0) > 0 && 
          !/\s$/.test(lastMessage.content ?? '');
        
        lastMessage.content = (lastMessage.content ?? '') + (needsSpaceAfterFunctionCall ? ' ' : '') + action.payload.textDelta;
        
        // Mark function call as inactive when we start receiving text deltas (function completed)
        if (lastMessage.functionCall?.isActive) {
          lastMessage.functionCall.isActive = false;
        }
      }
    },
    updateLastAssistantMessageWithFunctionCall: (state, action: PayloadAction<{ 
      functionCall?: { type: 'web_search' | 'unknown'; message: string; isActive: boolean };
      sources?: Array<{ title: string; url: string; snippet?: string }>;
    }>) => {
      // Add or update function call metadata and/or sources to the last assistant message
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      
      if (history.length > 0 && history[history.length - 1].role === 'assistant') {
        const lastMessage = history[history.length - 1];
        
        // Update function call if provided
        if (action.payload.functionCall) {
          lastMessage.functionCall = action.payload.functionCall;
        }
        
        // Update sources if provided
        if (action.payload.sources) {
          lastMessage.sources = action.payload.sources;
        }
      }
    },
    finalizeVoiceAssistantMessage: (state, action: PayloadAction<{ audioData?: string[]; sources?: Array<{ title: string; url: string; snippet?: string }> }>) => {
      // Finalize the last assistant voice message with audio data and sources
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      const audioData = action.payload.audioData;
      const sources = action.payload.sources;
      
      if (history.length > 0 && history[history.length - 1].role === 'assistant' && history[history.length - 1].isVoice) {
        const lastMessage = history[history.length - 1];
        
        // Add audio data if provided
        if (audioData && audioData.length > 0) {
          lastMessage.audioData = audioData;
        }
        
        // Add sources if provided
        if (sources && sources.length > 0) {
          lastMessage.sources = sources;
        }
        
        // Mark function call as inactive
        if (lastMessage.functionCall?.isActive) {
          lastMessage.functionCall.isActive = false;
        }
      }
    },
    finalizeVoiceUserMessage: (state, action: PayloadAction<{ transcript: string; audioData?: string | string[] }>) => {
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      const transcript = action.payload.transcript?.trim() || '';
      const audioData = action.payload.audioData;

      logger.log('[chatModeSlice] finalizeVoiceUserMessage called:', {
        hasTranscript: !!transcript,
        hasAudio: !!audioData,
        historyLength: history.length,
      });

      // Find the FIRST (earliest) user voice message that's still transcribing
      const message = history.find(m => m.role === 'user' && m.isVoice && m.isTranscribing);
      
      if (message) {
        logger.log('[chatModeSlice] Found transcribing message, updating with transcript and audio');
        // If transcript is empty or only whitespace, set to null (will be handled at render time)
        message.content = transcript || null;
        message.isTranscribing = false;
        if (audioData) {
          message.audioData = audioData;
        }
      } else {
        logger.warn('[chatModeSlice] No transcribing user message found to finalize');
      }
    },
    cancelTranscribingMessages: (state) => {
      // When voice mode is stopped abruptly:
      // 1. Mark all still-transcribing user messages as cancelled
      // 2. Remove any voice assistant messages with empty/null content — these are
      //    placeholder messages created when the assistant started responding but voice
      //    mode was stopped before the response was finalised. Leaving them in history
      //    causes displayChatHistory to enter the voice-merge branch, which hides
      //    subsequent translation results behind an empty/partial message.
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;

      // Iterate backwards so splicing does not shift unvisited indices
      for (let i = history.length - 1; i >= 0; i--) {
        if (history[i].role === 'user' && history[i].isVoice && history[i].isTranscribing) {
          logger.log('[chatModeSlice] Cancelling transcription for message:', history[i].id);
          history[i].content = null;
          history[i].isTranscribing = false;
        }
        // Remove voice assistant messages that were never finalised (content is empty/null)
        if (history[i].role === 'assistant' && history[i].isVoice && !history[i].content) {
          logger.log('[chatModeSlice] Removing incomplete voice assistant message:', history[i].id);
          history.splice(i, 1);
        }
      }
    },
  },
});

export const { 
  setChatMode, 
  setRagChatType, 
  setaskChatHistory, 
  setRagHistory, 
  clearaskChatHistory, 
  clearRagHistory, 
  setIsStreaming,
  addVoiceUserMessage,
  addVoiceAssistantMessage,
  updateLastAssistantMessage,
  updateLastAssistantMessageWithFunctionCall,
  finalizeVoiceUserMessage,
  finalizeVoiceAssistantMessage,
  cancelTranscribingMessages,
} = chatModeSlice.actions;
export const chatModeSelector = (state: { chatMode: ChatModeState }) => state.chatMode;
export default chatModeSlice.reducer;
